import { useCallback } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { AdditionalVehicleDetailsFormInputs } from '../schemas/additionalvehicledetails.schema';
import { estimateLastServiceMileage, localDaysBetween } from '../utils/lastServiceMileageEstimate';

interface Options {
    lastServiceDate: Date | undefined;
    mileage: number | null | undefined;
    annualMileage: number | null | undefined;
    getValues: UseFormGetValues<AdditionalVehicleDetailsFormInputs>;
    setValue: UseFormSetValue<AdditionalVehicleDetailsFormInputs>;
}

interface Result {
    lastServiceMileageEstimate: number | null;
    onLastServiceDatePicked: (date: Date | undefined) => void;
}

export function useLastServiceMileageEstimate({
    lastServiceDate,
    mileage,
    annualMileage,
    getValues,
    setValue,
}: Options): Result {
    const lastServiceMileageEstimate = estimateLastServiceMileage({
        currentMileage: mileage,
        annualMileage,
        lastServiceDate,
    });

    const onLastServiceDatePicked = useCallback(
        (date: Date | undefined) => {
            if (!date || localDaysBetween(date, new Date()) !== 0) return;
            const existing = getValues('lastServiceMileage');
            if (existing !== undefined && existing !== null) return;

            const estimate = estimateLastServiceMileage({
                currentMileage: getValues('mileage'),
                annualMileage: getValues('annualMileage'),
                lastServiceDate: date,
            });
            if (estimate === null) return;
            setValue('lastServiceMileage', estimate, { shouldValidate: true, shouldDirty: true });
        },
        [getValues, setValue],
    );

    return { lastServiceMileageEstimate, onLastServiceDatePicked };
}
